import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setBreadcrumbs } from '@app/redux/features/breadcrumbsSlice';
import { BreadCrumbIcon, BreadCrumbType } from '@entities/util/IBreadCrumb';
import { Typography, TextSize, Dropdown } from '@shared/index';
import { selectCurrentUser } from '@app/redux/features/userSlice';
import { PersonalResult, Salary, TeamResult } from '@widgets/index';
import { useGetManagers } from '@entities/managers-perfomance';
import { ManagersTabs } from './ManagersTabs';
import { ChevronDown, ChevronUp } from 'lucide-react';

const ManagersPerformancePage = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const [activeTab, setActiveTab] = useState<string>('personal');
  const [selectedManagerId, setSelectedManagerId] = useState<number | null>(null);

  const currentUserId = currentUser.user?.id ? Number(currentUser.user.id) : null;
  const isSalesTeam = Boolean(currentUser.user?.is_sales_team);
  const isAdmin = Boolean(currentUser.user?.is_admin);

  const managers = useGetManagers();
  const managersList = managers.data?.managers;

  const canEditPlans = isAdmin || currentUserId === 12;

  const dropdownItems =
    managersList?.map(manager => ({
      id: manager.id,
      label: manager.first_name,
    })) ?? [];

  useEffect(() => {
    if (!managersList?.length || selectedManagerId !== null) return;

    const managerWithId12 = managersList.find(manager => manager.id === 12);
    const defaultManagerId =
      managerWithId12?.id ?? (isSalesTeam && currentUserId ? currentUserId : managersList[0]?.id);

    setSelectedManagerId(defaultManagerId ?? null);
  }, [managersList, isSalesTeam, currentUserId, selectedManagerId]);

  useEffect(() => {
    dispatch(
      setBreadcrumbs([
        {
          title: 'Sales DB',
          type: BreadCrumbType.LINK,
          link: '/main',
          icon: BreadCrumbIcon.VIA_BTC,
        },
        { title: 'Показатели менеджеров', type: BreadCrumbType.TEXT },
      ])
    );
  }, [dispatch]);

  const handleManagerSelect = (managerId: number) => {
    setSelectedManagerId(managerId);
  };

  return (
    <>
      <div className='flex items-center mb-6'>
        <Typography
          size={TextSize.size32}
          title="Показатели менеджеров"
          className="font-semibold"
        />
        <div className="ml-auto max-w-[191px] mb-5 whitespace-nowrap">
          <Dropdown
            items={dropdownItems}
            selectedId={selectedManagerId}
            onSelect={handleManagerSelect}
            triggerClassName="flex items-center gap-2 px-[14px] py-[10px] rounded-[10px] border-none outline-none bg-[#202020] hover:bg-[#2A282C] w-full"
            contentClassName="bg-[#202020] text-white text-sm rounded-b-lg rounded-t-none p-2 shadow-lg z-50"
            itemClassName="flex items-center gap-3 px-[14px] py-[10px] rounded-lg hover:bg-[#2A282C]"
            activeItemClassName="bg-blue-500/20 border border-blue-500"
            closedIcon={<ChevronDown size={16} className="text-gray-400" />}
            openIcon={<ChevronUp size={16} className="text-gray-400" />}
          />
        </div>
      </div>
      <ManagersTabs value={activeTab} onChange={setActiveTab} />
      {activeTab === 'personal' && (
        <PersonalResult managerId={selectedManagerId} canEditPlans={canEditPlans} />
      )}
      {activeTab === 'team' && <TeamResult />}
      {activeTab === 'salary' && <Salary managerId={selectedManagerId} />}
    </>
  );
};

export default ManagersPerformancePage;
